import React, { useState } from 'react';
import styled from 'styled-components';
import { TYPE } from 'theme';
import Button from 'components/Button';
import Row from 'components/Row';
import { ReactComponent as ClockIcon } from 'assets/images/lucky-draw/clock.svg';
import { CircularProgress } from '@mui/material';
import { AutoColumn } from 'components/Column';

const TableContainer = styled.div`
    margin-top: 30px;
    background: rgba(0, 0, 0, 1);
    border-radius: 12px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    display: flex;
    justify-content: center;

    &::-webkit-scrollbar {
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(106, 13, 173, 0.5);
        border-radius: 4px;

        &:hover {
            background: rgba(106, 13, 173, 0.7);
        }
    }
`;

const TableContent = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 900px;
    border-radius: 12px;
    background: #000;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const EmptyWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 12px;
    min-height: 200px;
`;

const TableRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1.5fr;
    gap: 20px;
    align-items: center;
    padding: 24px;
    transition: background-color 0.3s ease;
    cursor: pointer;
    white-space: nowrap;
    border-radius: 8px;

    &:hover {
        background: rgba(40, 40, 40, 0.9);
    }
`;

const RoundInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const RoundTitle = styled(TYPE.main)`
    font-size: 16px;
    font-weight: 700;
    color: #fff;
`;

const DateRow = styled(Row)`
    gap: 6px;
    align-items: center;
`;

const ClockIconStyled = styled(ClockIcon)`
    width: 16px;
    height: 16px;
    color: #fff;
`;

const DateText = styled(TYPE.desc)`
    font-size: 14px;
    color: #fff;
`;

const RewardColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ActionColumn = styled(RewardColumn)`
    align-items: center;
    justify-content: center;
`;

const RewardAmount = styled(TYPE.main)<{ color?: string }>`
    font-size: 16px;
    font-weight: 700;
    color: ${({ color }) => color || '#fff'};
`;

const RewardLabel = styled(TYPE.desc)`
    font-size: 12px;
    color: #fff;
`;

const ClaimButton = styled(Button).attrs({
    type: 'outlineGradient',
    size: 'unset',
})`
    padding: 10px 24px !important;
    border-radius: 12px !important;
    white-space: nowrap;
    width: 76px;
    height: 36px;
`;

interface ClaimRecordData {
    round: number;
    date: string;
    tokenRewards: string;
    tokenName: string;
    btcRewards: string;
}

const ClaimRecord: React.FC = () => {
    const [loading] = useState(false);
    const records: ClaimRecordData[] = [];
    const handleClaim = (round: number) => {
        console.log('Claim round:', round);
        // TODO: claim reward
    };

    let content;
    if (loading) {
        content = (
            <LoadingWrapper>
                <CircularProgress />
            </LoadingWrapper>
        );
    } else if (!records.length) {
        content = (
            <EmptyWrapper>
                <TYPE.desc fontSize={16}>No Claim Records</TYPE.desc>
                <TYPE.desc fontSize={14} color="#666">
                    You don&apos;t have any claimable rewards yet.
                </TYPE.desc>
            </EmptyWrapper>
        );
    } else {
        content = (
            <TableContent>
                {records.map((item) => (
                    <TableRow key={item.round}>
                        <RoundInfo>
                            <RoundTitle># Round {item.round}</RoundTitle>
                            <DateRow>
                                <ClockIconStyled />
                                <DateText color="#999999">{item.date}</DateText>
                            </DateRow>
                        </RoundInfo>
                        <RewardColumn>
                            <RewardAmount>
                                + {item.tokenRewards} {item.tokenName}
                            </RewardAmount>
                            <RewardLabel color="#999999">Token Rewards</RewardLabel>
                        </RewardColumn>
                        <RewardColumn>
                            <RewardAmount color="#f7931a">+{item.btcRewards} BTC</RewardAmount>
                            <RewardLabel color="#999999">BTC Rewards</RewardLabel>
                        </RewardColumn>
                        <ActionColumn>
                            <ClaimButton onClick={() => handleClaim(item.round)}>Claim</ClaimButton>
                        </ActionColumn>
                    </TableRow>
                ))}
            </TableContent>
        );
    }

    return (
        <AutoColumn style={{ width: '100%' }} id="claim-record">
            <TYPE.main fontSize={16} fontWeight={700} mobileFz={14}>
                Claim Rewards
            </TYPE.main>
            <TableContainer>{content}</TableContainer>
        </AutoColumn>
    );
};

export default ClaimRecord;
